import { Component, DestroyRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';

import { ConsultantAttendanceRequest } from '../../core/models/consultant-attendance.model';
import { ConsultantAttendanceService } from '../../core/services/consultant-attendance.service';
import { CustomValidators } from '../../shared/validators';
import { ToastService } from '../../shared/services/toast.service';
import { WEBSUMMIT_PRODUCT_QUERY_PARAM } from '../../core/constants/websummit-contato.constants';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';

@Component({
  selector: 'umkt-websummit-contato',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './websummit-contato.component.html',
  styleUrls: ['./websummit-contato.component.scss'],
  host: {
    class: 'page-contact',
  },
})
export class WebsummitContatoComponent implements OnInit, OnDestroy {
  private static readonly BODY_CLASS = 'umkt-websummit-contato-page';
  private static readonly PRODUCT_STORAGE_KEY = 'umkt-websummit-product';
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly consultantAttendance = inject(ConsultantAttendanceService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentYear = new Date().getFullYear();

  submitted = false;
  showSuccess = false;
  isSubmitting = false;

  /** Campaign label from ?product= (withComponentInputBinding); not a hub catalog item. */
  readonly productLabel = input<string | undefined>(undefined);

  private resolvedProduct = '';

  readonly form = this.fb.group({
    cnpj: ['', [Validators.required, CustomValidators.cnpj]],
    nome: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(120)]],
    telefone: ['', [Validators.required, CustomValidators.phone]],
    telefone2: ['', [CustomValidators.phone]],
    cep: ['', [Validators.required, CustomValidators.cep]],
    clienteClaro: ['', Validators.required],
    empresa: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
  });

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      document.body.classList.add(WebsummitContatoComponent.BODY_CLASS);
    }

    this.captureProductParam();

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.captureProductParam();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.captureProductParam();
      });

    this.stripUtmFromUrl();
    this.captureProductParam();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      document.body.classList.remove(WebsummitContatoComponent.BODY_CLASS);
    }
  }

  private stripUtmFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    let removed = false;

    [...url.searchParams.keys()].forEach((key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        url.searchParams.delete(key);
        removed = true;
      }
    });

    if (!removed) {
      return;
    }

    const next = `${url.pathname}${url.search}${url.hash}`;
    history.replaceState(history.state, '', next);
  }

  get canSubmit(): boolean {
    return this.form.valid && !this.isSubmitting;
  }

  get canClear(): boolean {
    const v = this.form.getRawValue();
    return Object.values(v).some((value) => String(value ?? '').trim() !== '');
  }

  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = formatCnpj(input.value);
    this.form.controls.cnpj.setValue(masked, { emitEvent: true });
  }

  onPhoneInput(event: Event, controlName: 'telefone' | 'telefone2'): void {
    const digits = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 11);
    let masked = '';

    if (digits.length <= 2) {
      masked = digits.replace(/^(\d{0,2})/, '($1');
    } else if (digits.length <= 7) {
      masked = digits.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    } else {
      masked = digits.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }

    this.form.controls[controlName].setValue(masked, { emitEvent: true });
  }

  onCepInput(event: Event): void {
    const digits = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 8);
    const masked = digits.length > 5 ? digits.replace(/^(\d{5})(\d{0,3})/, '$1-$2') : digits;
    this.form.controls.cep.setValue(masked, { emitEvent: true });
  }

  showError(controlName: keyof typeof this.form.controls): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.touched || this.submitted));
  }

  errorMessage(controlName: keyof typeof this.form.controls): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';

    if (control.errors['required']) return 'Campo obrigatório.';

    if (control.errors['email']) return 'Informe um e-mail válido.';
    if (control.errors['minlength']) return 'Informe pelo menos 2 caracteres.';
    if (control.errors['invalidCNPJ']) return 'Informe um CNPJ válido.';
    if (control.errors['invalidPhone']) return 'Informe um telefone válido com DDD.';
    if (control.errors['invalidCEP']) return 'Informe um CEP válido (8 dígitos).';

    return 'Valor inválido.';
  }

  onSubmit(): void {
    this.submitted = true;
    this.showSuccess = false;

    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.captureProductParam();

    this.isSubmitting = true;

    this.consultantAttendance
      .submitConsultantAttendance(this.buildRequestPayload())
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.showSuccess = true;
          this.form.reset({
            cnpj: '',
            nome: '',
            email: '',
            telefone: '',
            telefone2: '',
            cep: '',
            clienteClaro: '',
            empresa: '',
          });
          this.submitted = false;
        },
        error: (error) => {
          const message =
            error?.error?.msg ??
            error?.error?.message ??
            'Não foi possível enviar seus dados. Tente novamente em instantes.';
          this.toastService.showError(message, 5000, 'top-center');
        },
      });
  }

  private buildRequestPayload(): ConsultantAttendanceRequest {
    const raw = this.form.getRawValue();
    const phone2 = this.onlyDigits(raw.telefone2 ?? '');

    const payload: ConsultantAttendanceRequest = {
      cnpj: unformatCnpj(raw.cnpj ?? ''),
      name: String(raw.nome ?? '').trim(),
      email: String(raw.email ?? '').trim(),
      phone: this.onlyDigits(raw.telefone ?? ''),
      zipCode: this.onlyDigits(raw.cep ?? ''),
      alreadyClaroCustomer: raw.clienteClaro === 'sim',
      companyName: String(raw.empresa ?? '').trim(),
      product: this.resolvedProduct || null,
    };

    if (phone2) {
      payload.phone2 = phone2;
    }

    return payload;
  }

  private captureProductParam(): void {
    const resolved = this.resolveProductParam();
    if (resolved) {
      this.resolvedProduct = resolved;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(WebsummitContatoComponent.PRODUCT_STORAGE_KEY, resolved);
      }
    }
  }

  private resolveProductParam(): string {
    const sources: (string | null | undefined)[] = [
      this.productLabel(),
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get(WEBSUMMIT_PRODUCT_QUERY_PARAM)
        : null,
      this.route.snapshot.queryParamMap.get(WEBSUMMIT_PRODUCT_QUERY_PARAM),
      this.route.snapshot.queryParams[WEBSUMMIT_PRODUCT_QUERY_PARAM] as string | undefined,
    ];

    if (typeof window !== 'undefined') {
      const match = window.location.href.match(/[?&]product=([^&#]*)/i);
      if (match?.[1]) {
        sources.push(match[1]);
      }
    }

    if (typeof window !== 'undefined') {
      sources.push(sessionStorage.getItem(WebsummitContatoComponent.PRODUCT_STORAGE_KEY));
    }

    for (const source of sources) {
      const normalized = this.normalizeProductQueryValue(source);
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  /** ?product=Claro%20GPUaaS → "Claro GPUaaS" */
  private normalizeProductQueryValue(value: string | null | undefined): string | null {
    if (value == null) {
      return null;
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }

    const needsDecode = /%[0-9A-Fa-f]{2}/.test(raw) || raw.includes('+');
    if (!needsDecode) {
      return raw;
    }

    try {
      return decodeURIComponent(raw.replace(/\+/g, ' ')).trim() || null;
    } catch {
      return raw;
    }
  }

  private onlyDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  onClear(): void {
    this.form.reset({
      cnpj: '',
      nome: '',
      email: '',
      telefone: '',
      telefone2: '',
      cep: '',
      clienteClaro: '',
      empresa: '',
    });
    this.submitted = false;
    this.showSuccess = false;
  }
}
